// FontFace objects already registered with document.fonts
const loadedFaces = new Map();

export function glyphFontFamily(font) {
  return `kbfont-${font.keyboard_type}-${font.keyboard_id}`;
}

export function dropGlyphFontFace(family) {
  const face = loadedFaces.get(family);
  if (face) document.fonts.delete(face);
  loadedFaces.delete(family);
}

export async function ensureGlyphFontFace(font) {
  const family = glyphFontFamily(font);
  if (loadedFaces.has(family)) return family;
  const binary = atob(font.font_data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const face = new FontFace(family, bytes.buffer);
  await face.load();
  document.fonts.add(face);
  loadedFaces.set(family, face);
  return family;
}

export function glyphText(font, text) {
  const map = font?.glyph_codepoints || {};
  const keys = Object.keys(map).sort((a, b) => b.length - a.length);
  if (!keys.length) return text;
  let out = "";
  let i = 0;
  while (i < text.length) {
    const key = keys.find((k) => text.startsWith(k, i));
    if (key) {
      out += map[key];
      i += key.length;
    } else {
      out += text[i];
      i += 1;
    }
  }
  return out;
}

export function maskBBox(mask) {
  if (!mask || !mask.length) return null;
  const xs = mask.map((pt) => pt[0]);
  const ys = mask.map((pt) => pt[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    minX,
    minY,
    width: Math.max(...xs) - minX,
    height: Math.max(...ys) - minY,
  };
}

function clipMask(ctx, mask) {
  ctx.beginPath();
  mask.forEach(([x, y], i) => {
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
  ctx.clip();
}

// paints the page image exactly where the line panel shows it
function drawPlacedUnderlay(ctx, image, mask, clipToMask, placement) {
  const { ratio, angle, left, top, originX, originY } = placement;
  ctx.save();
  ctx.globalAlpha = 0.35;
  ctx.translate(left, top);
  ctx.translate(originX, originY);
  ctx.rotate((angle * Math.PI) / 180);
  ctx.translate(-originX, -originY);
  ctx.scale(ratio, ratio);
  if (clipToMask && mask?.length) clipMask(ctx, mask);
  ctx.drawImage(image, 0, 0);
  ctx.restore();
}

export async function drawGlyphLine(canvas, options) {
  const { font, text, isRtl, width, height, image, mask, clipToMask, placement } =
    options;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  canvas.width = width;
  canvas.height = height;

  ctx.fillStyle = "#f7f3ea";
  ctx.fillRect(0, 0, width, height);

  const bbox = maskBBox(mask);
  if (image && placement) {
    drawPlacedUnderlay(ctx, image, mask, clipToMask, placement);
  } else if (image && bbox && bbox.width && bbox.height) {
    ctx.save();
    ctx.globalAlpha = 0.35;
    if (clipToMask) {
      // Clip to the line's polygon mask shape.
      ctx.scale(width / bbox.width, height / bbox.height);
      ctx.translate(-bbox.minX, -bbox.minY);
      clipMask(ctx, mask);
      ctx.drawImage(image, 0, 0);
    } else {
      // Plain rectangular bounding-box crop, no polygon clip.
      ctx.drawImage(
        image,
        bbox.minX,
        bbox.minY,
        bbox.width,
        bbox.height,
        0,
        0,
        width,
        height
      );
    }
    ctx.restore();
  }

  if (!text || !font) return;

  let family;
  try {
    family = await ensureGlyphFontFace(font);
  } catch (e) {
    console.error("Error loading glyph font face:", e);
    return;
  }

  const drawText = glyphText(font, text);
  const pad = Math.max(8, width * 0.01);
  let fontSize = height * 0.9;
  ctx.font = `${fontSize}px "${family}", sans-serif`;
  const textWidth = ctx.measureText(drawText).width;
  if (textWidth > width - 2 * pad) {
    fontSize *= (width - 2 * pad) / textWidth;
    ctx.font = `${fontSize}px "${family}", sans-serif`;
  }

  ctx.fillStyle = "#241a10";
  ctx.direction = isRtl ? "rtl" : "ltr";
  ctx.textAlign = isRtl ? "right" : "left";
  ctx.textBaseline = "alphabetic";
  const inkHeight = fontSize * 0.62;
  const baselineY = (height + inkHeight) / 2;
  const x = isRtl ? width - pad : pad;
  ctx.fillText(drawText, x, baselineY);
}
